import React, { ReactElement, useState } from "react";
import { ThemeProvider, responsiveFontSizes, StyledEngineProvider } from "@mui/material/styles";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import { ContextPlayer, PlayerContext } from "../contexts/PlayerContext";
import { lightTheme } from "../themes/lightTheme";
import { darkTheme } from "../themes/darkTheme";
import { AlertProvider } from "../contexts/alert/AlertProvider";

import { CssBaseline, useMediaQuery } from "@mui/material";
import { AuthProvider } from "../auth/AuthProvider";
import { FirebaseProvider } from "../analytics/FirebaseProvider";
import { FirebaseApp } from "firebase/app";
import { Analytics } from "firebase/analytics";
import { TrackingProvider } from "../analytics/TrackingProvider";
import UserApp from "./UserApp";
import AdminApp from "./AdminApp";
import { DebugProvider } from "../features/debugger/DebugProvider";

function AppRouter(): ReactElement {
    const location = useLocation();
    
    // Route to appropriate app based on path
    if (location.pathname.startsWith('/admin')) {
        return <AdminApp />;
    }
    
    return <UserApp />;
}

export default function App(): ReactElement {
    const [firebaseApp, setFirebaseApp] = useState<FirebaseApp>();
    const [firebaseAnalytics, setFirebaseAnalytics] = useState<Analytics>();
    const [player, setPlayer] = useState<ContextPlayer>();
    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

    function updateContextPlayer(name: string, isModerator: boolean) {
        if (player) {
            setPlayer(new ContextPlayer(player.id, name, isModerator));
        } else {
            console.warn("could not update the player context because it is undefined")
        }
    }

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={responsiveFontSizes(prefersDarkMode ? darkTheme : lightTheme)}>
                <CssBaseline />
                <DebugProvider>
                    <FirebaseProvider
                        onAppInitialized={(app) => {
                            setFirebaseApp(app);
                        }}
                        onAnalyticsInitialized={(analytics) => setFirebaseAnalytics(analytics)}
                    >
                        <TrackingProvider firebaseAnalytics={firebaseAnalytics}>
                            <PlayerContext.Provider value={{ player: player, setPlayer: setPlayer, updatePlayer: updateContextPlayer }}>
                                <AuthProvider firebaseApp={firebaseApp}>
                                    <AlertProvider>
                                        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                                            <AppRouter />
                                        </Router>
                                    </AlertProvider>
                                </AuthProvider>
                            </PlayerContext.Provider>
                        </TrackingProvider>
                    </FirebaseProvider>
                </DebugProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
}
